// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { TriageSortColumnKey } from './triage-sort-fields';
import { TRIAGE_SORT_COLUMN_KEYS } from './triage-sort-fields';

/**
 * System for custom Polaris triage codes.
 */
export const TRIAGE_OBS_CODE_SYSTEM = 'http://polarissleep.com/codes/triage';

/**
 * System for triage recommendation category codes.
 */
export const TRIAGE_OBS_RECOMMENDATION_SYSTEM = 'http://polarissleep.com/codes/triage-recommendation';

/**
 * System for triage insurance eligibility codes.
 */
export const TRIAGE_OBS_INSURANCE_SYSTEM = 'http://polarissleep.com/codes/triage-insurance';

/**
 * System for Observation category codes (distinguishes patient-latest vs intake-snapshot).
 */
export const TRIAGE_OBS_CATEGORY_SYSTEM = 'http://polarissleep.com/codes/triage-observation-category';

/**
 * System for Observation identifier (triage metric tracking).
 */
export const TRIAGE_OBS_IDENTIFIER_SYSTEM = 'http://polarissleep.com/identifier/triage';

/**
 * LOINC code system URL.
 */
export const LOINC_SYSTEM = 'http://loinc.org';

/**
 * Observation category codes that distinguish the three triage series.
 */
export const TRIAGE_OBS_CATEGORY = {
  patientLatest: 'patient-latest-triage',
  intakeSnapshot: 'intake-snapshot-triage',
  patientLatestNullish: 'patient-latest-triage-nullish',
} as const;

/**
 * Prefix for nullish marker Observation identifiers.
 * Combined with patientId and code to create a unique deterministic identifier.
 */
export const TRIAGE_OBS_NULLISH_IDENTIFIER_PREFIX = 'nullish';

/**
 * Builds a deterministic identifier value for a nullish triage marker Observation.
 * Format: "nullish|<patientId>|<code>"
 * The `nullish|` prefix distinguishes marker identifiers from real Observation identifiers
 * (which start with a Composition ID), ensuring no identifier collision.
 * This enables idempotent UPSERT operations on re-run.
 */
export function buildNullishTriageMarkerIdentifier(patientId: string, code: string): string {
  return `${TRIAGE_OBS_NULLISH_IDENTIFIER_PREFIX}|${patientId}|${code}`;
}

/**
 * Specification for a single triage Observation code.
 * Includes the system, code, FHIR value field type, and optional unit info.
 */
export interface TriageObsCodeSpec {
  /** TriageSortColumnKey (existing) */
  columnKey: Exclude<TriageSortColumnKey, 'name'>;
  /** Observation code system (LOINC or Polaris custom) */
  system: string;
  /** Observation code value */
  code: string;
  /** FHIR value field on the Observation */
  valueField: 'valueQuantity' | 'valueCodeableConcept';
  /** UCUM unit code for quantity values (omitted for valueCodeableConcept) */
  unit?: { display: string; code: string };
}

/**
 * Column keys that correspond to Polaris triage Observation resources (Intake/Ineligible tabs).
 * Testing-tab columns (ahi, ahiCentral, etc.) use CLOUDPAT observations instead and are
 * intentionally excluded here.
 */
export type TriageIntakeSortColumnKey = Exclude<
  TriageSortColumnKey,
  | 'name'
  | 'ahi'
  | 'ahiCentral'
  | 'totalSleepTime'
  | 'validSleepTime'
  | 'satBelow88'
  | 'meanNadir'
  | 'intakeDate'
  | 'systemStatus'
>;

/**
 * Catalog of all triage Observation codes, keyed by column key.
 * Each entry specifies the code, system, value field type, and unit.
 * This is the single source of truth for triage metric codes.
 * Note: excludes 'name' and Testing-tab CLOUDPAT columns.
 */
export const TRIAGE_OBS_CODES: Record<TriageIntakeSortColumnKey, TriageObsCodeSpec> = {
  'stop-bang': {
    columnKey: 'stop-bang',
    system: TRIAGE_OBS_CODE_SYSTEM,
    code: 'stop-bang-score',
    valueField: 'valueQuantity',
    unit: { display: '{score}', code: '{score}' },
  },
  epworth: {
    columnKey: 'epworth',
    system: TRIAGE_OBS_CODE_SYSTEM,
    code: 'epworth-score',
    valueField: 'valueQuantity',
    unit: { display: '{score}', code: '{score}' },
  },
  bmi: {
    columnKey: 'bmi',
    system: LOINC_SYSTEM,
    code: '39156-5',
    valueField: 'valueQuantity',
    unit: { display: 'kg/m2', code: 'kg/m2' },
  },
  age: {
    columnKey: 'age',
    system: LOINC_SYSTEM,
    code: '30525-0',
    valueField: 'valueQuantity',
    unit: { display: 'a', code: 'a' },
  },
  exclusions: {
    columnKey: 'exclusions',
    system: TRIAGE_OBS_CODE_SYSTEM,
    code: 'exclusions-count',
    valueField: 'valueQuantity',
    unit: { display: '{count}', code: '{count}' },
  },
  comorbidities: {
    columnKey: 'comorbidities',
    system: TRIAGE_OBS_CODE_SYSTEM,
    code: 'comorbidities-count',
    valueField: 'valueQuantity',
    unit: { display: '{count}', code: '{count}' },
  },
  recommendation: {
    columnKey: 'recommendation',
    system: TRIAGE_OBS_CODE_SYSTEM,
    code: 'recommendation-category',
    valueField: 'valueCodeableConcept',
  },
  insurance: {
    columnKey: 'insurance',
    system: TRIAGE_OBS_CODE_SYSTEM,
    code: 'insurance-eligibility',
    valueField: 'valueCodeableConcept',
  },
};
